'use client';

import { useState, useEffect, useRef, use } from 'react';
import Link from 'next/link';
import ShareButtons from '@/app/components/ShareButtons';
import { getTotalPrice, getFormattedTotal, getPerCardAverage, getMarginalCost, applyPromoToTotal, type PromoCode } from '@/app/lib/pricing';

const MAX_CHARS = Number(process.env.NEXT_PUBLIC_MAX_CHARS) || 270;

const repSlug = (first: string, last: string) =>
  `${first}-${last}`.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const getRepId = (rep: any): string => rep?.references?.bioguide_id || rep?.id || '';
const getRepFullName = (rep: any): string =>
  rep?.name || `${rep?.bio?.first_name || ''} ${rep?.bio?.last_name || ''}`.trim() || 'Unknown';
const getRepTitle = (rep: any): string => {
  if (rep?.level === 'state') {
    if (rep?.jurisdiction === 'dc') return 'Council Member';
    return rep?.chamber === 'upper' ? 'State Senator' : 'State Representative';
  }
  return rep?.type === 'senator' ? 'U.S. Senator' : 'U.S. Representative';
};
const getRepState = (rep: any): string =>
  (rep?.jurisdiction || rep?.bio?.state || '').toUpperCase();
const getRepParty = (rep: any): string => rep?.party || rep?.bio?.party || 'Unknown';

const US_STATES = [
  ['al','Alabama'],['ak','Alaska'],['az','Arizona'],['ar','Arkansas'],['ca','California'],
  ['co','Colorado'],['ct','Connecticut'],['de','Delaware'],['fl','Florida'],['ga','Georgia'],
  ['hi','Hawaii'],['id','Idaho'],['il','Illinois'],['in','Indiana'],['ia','Iowa'],
  ['ks','Kansas'],['ky','Kentucky'],['la','Louisiana'],['me','Maine'],['md','Maryland'],
  ['ma','Massachusetts'],['mi','Michigan'],['mn','Minnesota'],['ms','Mississippi'],['mo','Missouri'],
  ['mt','Montana'],['ne','Nebraska'],['nv','Nevada'],['nh','New Hampshire'],['nj','New Jersey'],
  ['nm','New Mexico'],['ny','New York'],['nc','North Carolina'],['nd','North Dakota'],['oh','Ohio'],
  ['ok','Oklahoma'],['or','Oregon'],['pa','Pennsylvania'],['ri','Rhode Island'],['sc','South Carolina'],
  ['sd','South Dakota'],['tn','Tennessee'],['tx','Texas'],['ut','Utah'],['vt','Vermont'],
  ['va','Virginia'],['wa','Washington'],['wv','West Virginia'],['wi','Wisconsin'],['wy','Wyoming'],
  ['dc','Washington D.C.'],['pr','Puerto Rico'],
] as const;

type StepStr = 'search' | 'select';

export default function SharedMessagePage({ params }: { params: Promise<{ id: string }> }) {
  const resolvedParams = use(params);
  const messageId = resolvedParams.id;

  const [step, setStep] = useState<StepStr>('search');
  const [loading, setLoading] = useState(true);
  const urlPromoRef = useRef<string | null>(null); // captured before replaceState strips it
  const vsSlugARef = useRef<string | null>(null);
  const vsSlugBRef = useRef<string | null>(null);

  // Track both the original message and the current edits
  const [originalMessage, setOriginalMessage] = useState('');
  const [message, setMessage] = useState('');
  const [slug, setSlug] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const [sendCount, setSendCount] = useState(0);
  const [searchMode, setSearchMode] = useState<'address' | 'name'>('address');
  const [address, setAddress] = useState('');
  const [repName, setRepName] = useState('');
  const [cart, setCart] = useState<any[]>([]);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState<{promo: PromoCode, label: string} | null>(null);
  const upsellShownAtRef = useRef<number | null>(null);
  const [promoError, setPromoError] = useState('');
  const [showPromoInput, setShowPromoInput] = useState(false);
  const [promoLoading, setPromoLoading] = useState(false);
  const [okToContact, setOkToContact] = useState(true);
  const [subscribeNewsletter, setSubscribeNewsletter] = useState(true);
  const [govLevels, setGovLevels] = useState<Set<'federal' | 'state'>>(new Set(['federal']));
  const [stateJurisdiction, setStateJurisdiction] = useState('');

  const cartTotal = (qty: number) => appliedPromo
    ? applyPromoToTotal(getTotalPrice(qty), appliedPromo.promo, qty)
    : getTotalPrice(qty);

  const cartIds = cart.map((r: any) => getRepId(r));

  // ==========================================
  // BROWSER BACK BUTTON SUPPORT
  // ==========================================
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const modeParam = params.get('mode');
    urlPromoRef.current = params.get('promo'); // capture before replaceState strips it
    vsSlugARef.current = params.get('vs_a');
    vsSlugBRef.current = params.get('vs_b');
    if (modeParam === 'name') setSearchMode('name');
    window.history.replaceState(
      { step: 'search' }, '',
      modeParam === 'name' ? '?mode=name#search' : '?mode=address#search'
    );

    const handlePopState = (event: PopStateEvent) => {
      if (event.state && event.state.step) {
        setStep(event.state.step as StepStr);
      } else {
        setStep('search');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const goToStep = (newStep: StepStr) => {
    setStep(newStep);
    const prefix = searchMode === 'name' ? '?mode=name' : '?mode=address';
    window.history.pushState({ step: newStep }, '', `${prefix}#${newStep}`);
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    if (text.length <= MAX_CHARS) setMessage(text);
  };

  useEffect(() => {
    const fetchMessage = async () => {
      try {
        const res = await fetch(`/api/messages/${messageId}`);
        if (res.ok) {
          const data = await res.json();
          setMessage(data.content);
          setOriginalMessage(data.content);
          setSendCount(data.send_count);
          setSlug(data.slug || null);
        } else {
          setMessage("Message not found. It may have been removed.");
        }
      } catch (error) {
        setMessage("Error loading message.");
      } finally {
        setLoading(false);
        // Auto-apply URL promo after page is fully loaded.
        // Read from ref (captured in back-button effect before replaceState stripped it).
        const urlPromo = urlPromoRef.current;
        if (urlPromo) {
          setPromoCode(urlPromo);
          setShowPromoInput(true);
          fetch('/api/promo', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code: urlPromo, quantity: 1 }),
          })
            .then(r => r.json())
            .then(data => {
              if (data.valid) {
                setAppliedPromo({ promo: data.promo, label: data.label });
              }
            })
            .catch(() => {});
        }
      }
    };
    fetchMessage();
  }, [messageId]);

  // ==========================================
  // SEARCH
  // ==========================================
  const runSearch = async () => {
    setLoading(true);
    try {
      const levels = Array.from(govLevels);
      let payload: Record<string, unknown>;
      if (searchMode === 'name') {
        if (govLevels.has('state') && !stateJurisdiction) {
          alert('Please select a state to search state legislators by name.');
          setLoading(false);
          return null;
        }
        payload = { name: repName, levels, ...(govLevels.has('state') ? { jurisdiction: stateJurisdiction } : {}) };
      } else {
        payload = { address, levels };
      }
      const response = await fetch('/api/reps', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (response.ok) {
        const legislators = [
          ...(data.federalLegislators || []),
          ...(data.stateLegislators || []),
        ];
        if (legislators.length === 0) {
          alert(data.error || "No representatives found for that address.");
          return null;
        }
        setSearchResults(legislators);
        return legislators;
      } else {
        alert(data.error || "Something went wrong searching for reps.");
        return null;
      }
    } catch (error) {
      alert("Failed to connect to the server.");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const legislators = await runSearch();
    if (legislators) {
      addAllToCart(legislators);
      goToStep('select');
    }
  };

  const handleSearchAgain = async (e: React.FormEvent) => {
    e.preventDefault();
    await runSearch();
    // Stay on select step — results replace the top zone, cart unchanged
  };

  // ==========================================
  // CART
  // ==========================================
  const addToCart = (rep: any) => {
    const id = getRepId(rep);
    if (!id || cartIds.includes(id)) return;
    setCart(prev => [...prev, rep]);
  };

  const addAllToCart = (legislators: any[]) => {
    setCart(prev => {
      const existingIds = prev.map((r: any) => getRepId(r));
      const toAdd = legislators.filter((r: any) => getRepId(r) && !existingIds.includes(getRepId(r)));
      return [...prev, ...toAdd];
    });
  };

  const removeFromCart = (id: string) => {
    setCart(prev => prev.filter((r: any) => getRepId(r) !== id));
  };

  // ==========================================
  // CHECKOUT
  // ==========================================
  const handleCheckout = async () => {
    setIsCheckingOut(true);

    const selectedRepIds = cart.map((r: any) => getRepId(r));
    const repNames = cart.map((rep: any) => {
      const title = getRepTitle(rep).replace('U.S. ', '');
      return `${title} ${getRepFullName(rep)}`.trim();
    });
    const repTypes = cart.map((rep: any) => {
      if (rep?.level === 'state') return '';
      return rep?.type === 'senator' ? 'S' : 'H';
    });
    const repAddresses = cart.map((rep: any) => {
      if (rep?.level === 'state') return rep.capitol_address ?? null;
      return null;
    });

    try {
      const response = await fetch('/api/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message, selectedRepIds, repNames, repTypes, repAddresses, address, promoCode: appliedPromo ? promoCode : '', okToContact, subscribeNewsletter, vsSlugA: vsSlugARef.current, vsSlugB: vsSlugBRef.current }),
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert(data.error || "Checkout failed. Please try again.");
      }
    } catch (error) {
      alert("Network error occurred during checkout.");
    } finally {
      setIsCheckingOut(false);
    }
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoError('');
    setAppliedPromo(null);
    try {
      const res = await fetch('/api/promo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: promoCode, quantity: cart.length }),
      });
      const data = await res.json();
      if (data.valid) {
        setAppliedPromo({ promo: data.promo, label: data.label });
      } else {
        setPromoError(data.error || 'Invalid promo code');
      }
    } catch {
      setPromoError('Could not validate code. Try again.');
    } finally {
      setPromoLoading(false);
    }
  };

  if (loading && !message) {
    return <div className="min-h-screen flex items-center justify-center text-white">Loading campaign...</div>;
  }

  const isEdited = originalMessage && message !== originalMessage;

  const shareUrl = `https://postcardstocongress.org/m/${slug || messageId}`;
  const handleCopy = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    if (typeof window.umami !== 'undefined') window.umami.track('campaign_link_copied');
  };

  // Rep card renderer (shared between results and cart)
  const renderRepCard = (rep: any, index: number, inCart: boolean) => {
    const id = getRepId(rep) || `fallback-id-${index}`;
    const fullName = getRepFullName(rep);
    const roleLabel = getRepTitle(rep);
    const stateAbbr = getRepState(rep);
    const role = stateAbbr ? `${roleLabel} · ${stateAbbr}` : roleLabel;
    const party = getRepParty(rep);
    const partyColor = party === 'Democrat' || party === 'Democratic' ? 'text-blue-600' : party === 'Republican' ? 'text-red-600' : 'text-slate-500';
    const alreadyInCart = cartIds.includes(id);
    const repLink = rep?.level === 'state'
      ? null
      : `/rep/${repSlug(rep?.bio?.first_name || '', rep?.bio?.last_name || '')}`;

    if (inCart) {
      return (
        <div key={id} className="relative p-5 rounded-2xl border-2 border-amber-500 bg-white shadow-lg">
          <button
            onClick={() => removeFromCart(id)}
            className="absolute top-3 right-3 h-7 w-7 rounded-full bg-slate-100 hover:bg-red-100 hover:text-red-600 text-slate-400 text-sm font-bold flex items-center justify-center transition-colors"
            title="Remove"
          >✕</button>
          <div className="pr-8">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">{role}</p>
            <h3 className="text-lg font-bold text-slate-900 leading-tight">
              {repLink
                ? <Link href={repLink} target="_blank" className="hover:text-amber-700 transition-colors">{fullName}</Link>
                : fullName}
            </h3>
            <p className={`text-sm font-medium mt-1 ${partyColor}`}>{party}</p>
          </div>
        </div>
      );
    }

    return (
      <div key={id} className="relative p-5 rounded-2xl border-2 border-slate-200 bg-amber-50/20 flex flex-col gap-3">
        <div className="pr-2">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">{role}</p>
          <h3 className="text-lg font-bold text-slate-900 leading-tight">
            {repLink
              ? <Link href={repLink} target="_blank" className="hover:text-amber-700 transition-colors">{fullName}</Link>
              : fullName}
          </h3>
          <p className={`text-sm font-medium mt-1 ${partyColor}`}>{party}</p>
        </div>
        <button
          onClick={() => addToCart(rep)}
          disabled={alreadyInCart}
          className={`mt-auto w-full py-2 rounded-xl text-sm font-bold transition-colors ${
            alreadyInCart
              ? 'bg-green-100 text-green-700 cursor-default'
              : 'bg-amber-500 hover:bg-amber-600 text-white active:scale-95'
          }`}
        >
          {alreadyInCart ? '✓ Added' : 'Add to List →'}
        </button>
      </div>
    );
  };

  const newResultsNotInCart = searchResults.filter(
    (r: any) => !cartIds.includes(getRepId(r))
  );

  return (
    <main className="min-h-screen flex flex-col items-center p-4 sm:p-6">

      {/* STEP 1: READ/EDIT MESSAGE & SEARCH */}
      {step === 'search' && (
        <div className="max-w-6xl w-full mt-8 sm:mt-12 animate-in fade-in z-10 px-4">

          <div className="text-center mb-8">
            <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mb-2">Send this <span className="bg-gradient-to-r from-red-500 to-amber-400 bg-clip-text text-transparent">message</span> to your <span className="bg-gradient-to-r from-blue-400 to-red-400 bg-clip-text text-transparent">reps</span></h1>
            {!isEdited ? (
              <p className="text-lg text-slate-300 font-medium">This campaign has already been sent to Congress <span className="text-red-500 font-bold">{sendCount} times</span>.</p>
            ) : (
              <p className="text-lg text-amber-400 font-medium">You are creating a brand new personalized campaign.</p>
            )}
            <p className="mt-2">
              <Link href={`/vs?a=${messageId}`} className="text-xs text-slate-500 hover:text-amber-400 transition-colors">
                ⚔️ Compare with another campaign →
              </Link>
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">

            {/* LEFT SIDE: MESSAGE EDITOR & ADDRESS */}
            <div className="bg-white p-6 sm:p-8 rounded-3xl shadow-2xl border border-slate-100 flex flex-col h-full">

              {isEdited && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-xl mb-6 text-sm flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
                  <div>
                    ✏️ <strong>You've personalized this message!</strong> You will get a fresh shareable link and your own sender counter after checkout.
                  </div>
                  <button
                    onClick={() => setMessage(originalMessage)}
                    className="px-4 py-2 bg-amber-200 hover:bg-amber-300 text-amber-900 rounded-lg text-xs font-bold transition-colors shadow-sm whitespace-nowrap active:scale-95"
                  >
                    ↺ Reset
                  </button>
                </div>
              )}

              <div className="bg-slate-50 border-2 border-slate-200 focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-500/20 rounded-2xl p-5 mb-8 transition-all flex flex-col">
                <p className="font-bold mb-2 text-slate-900 font-serif">Dear Representative(s),</p>
                <textarea
                  value={message}
                  onChange={handleMessageChange}
                  className="w-full bg-transparent text-slate-900 text-lg font-serif leading-relaxed resize-y min-h-[150px] outline-none"
                  placeholder="Write your message here..."
                  required
                />
                <p className="mt-2 text-slate-900 font-serif">Sincerely,<br/><span className="italic text-slate-500">[Your Name - Added at Checkout]</span></p>

                <div className="flex justify-between mt-4 text-sm font-medium border-t border-slate-200 pt-3">
                  <p className="text-slate-400">Keep it concise.</p>
                  <p className={message.length > MAX_CHARS * 0.9 ? "text-red-600 font-bold" : "text-slate-400"}>
                    {message.length} / {MAX_CHARS}
                  </p>
                </div>
              </div>

              {/* SHARE / CAMPAIGN LINK */}
              {!isEdited ? (
                <div className="flex items-center gap-2 bg-[#07111e] border border-amber-500/20 rounded-xl px-4 py-3 mb-4">
                  <span className="text-amber-400 text-sm shrink-0">🔗</span>
                  <span className="text-slate-400 text-xs truncate flex-1 font-mono">{shareUrl}</span>
                  <button
                    onClick={handleCopy}
                    className="shrink-0 text-xs font-bold px-3 py-1.5 rounded-lg transition-all bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/20"
                  >
                    {copied ? '✓ Copied!' : 'Copy Link'}
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 bg-[#07111e] border border-slate-700/50 rounded-xl px-4 py-3 mb-4">
                  <span className="text-slate-500 text-sm shrink-0">✏️</span>
                  <p className="text-slate-500 text-xs">
                    You&apos;ve customized this message. Send the first postcard to start your own campaign.
                  </p>
                </div>
              )}

              {/* Level + Find By toggles */}
              <div className="rounded-xl overflow-hidden border border-slate-200 mb-3 mt-auto">
                <div role="radiogroup" aria-label="Level of government">
                  <div className="w-full text-center text-xs font-bold uppercase tracking-widest py-1.5 bg-slate-100 text-slate-500">
                    Level
                  </div>
                  <div className="flex gap-1 bg-slate-50 p-1 text-sm font-semibold">
                    {(['federal', 'state'] as const).map(lvl => {
                      const isActive = govLevels.has(lvl);
                      return (
                        <button
                          key={lvl}
                          type="button"
                          role="radio"
                          aria-checked={isActive}
                          onClick={() => setGovLevels(new Set([lvl]))}
                          className={`flex-1 py-1.5 rounded-lg transition-all cursor-pointer ${
                            isActive
                              ? lvl === 'federal' ? 'bg-red-700 text-white shadow-sm' : 'bg-amber-600 text-white shadow-sm'
                              : 'text-slate-500 hover:text-slate-700'
                          }`}
                        >
                          {lvl === 'federal' ? 'Federal' : 'State'}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="border-t border-slate-200" />
                <div role="radiogroup" aria-label="Search method">
                  <div className="w-full text-center text-xs font-bold uppercase tracking-widest py-1.5 bg-slate-100 text-slate-500">
                    Find By
                  </div>
                  <div className="flex gap-1 bg-slate-50 p-1 text-sm font-semibold">
                    <button
                      type="button"
                      role="radio"
                      aria-checked={searchMode === 'address'}
                      onClick={() => { setSearchMode('address'); window.history.replaceState(null, '', '?mode=address#search'); }}
                      className={`flex-1 py-1.5 rounded-lg transition-all ${searchMode === 'address' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                      By Address
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={searchMode === 'name'}
                      onClick={() => { setSearchMode('name'); window.history.replaceState(null, '', '?mode=name#search'); }}
                      className={`flex-1 py-1.5 rounded-lg transition-all ${searchMode === 'name' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                      By Name
                    </button>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
                {searchMode === 'address' ? (
                  <input
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Your street address..."
                    required
                    className="flex-1 px-5 py-4 rounded-xl border border-slate-200 focus:ring-4 outline-none text-lg text-slate-900"
                  />
                ) : (
                  <div className="flex flex-col sm:flex-row gap-2 flex-1">
                    {govLevels.has('state') && (
                      <select
                        value={stateJurisdiction}
                        onChange={e => setStateJurisdiction(e.target.value)}
                        required={govLevels.has('state')}
                        className="px-4 py-4 rounded-xl border border-slate-200 focus:ring-4 outline-none text-base text-slate-900 bg-white"
                      >
                        <option value="">Select state…</option>
                        {US_STATES.map(([abbr, label]) => (
                          <option key={abbr} value={abbr}>{label}</option>
                        ))}
                      </select>
                    )}
                    <input
                      type="text"
                      value={repName}
                      onChange={(e) => setRepName(e.target.value)}
                      placeholder="e.g. Bernard Sanders, Ted Cruz..."
                      required
                      className="flex-1 px-5 py-4 rounded-xl border border-slate-200 focus:ring-4 outline-none text-lg text-slate-900"
                    />
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading || !message.trim() || (searchMode === 'address' ? !address.trim() : !repName.trim())}
                  className="px-8 py-4 bg-red-700 hover:bg-red-800 disabled:bg-slate-300 disabled:text-slate-500 text-white font-bold rounded-xl text-lg shadow-md transition-colors whitespace-nowrap active:scale-95"
                >
                  {loading ? 'Searching...' : 'Find My Reps'}
                </button>
              </form>
              <p className="text-xs text-slate-500 mt-4 text-center">
                By using this site, you agree to our <Link href="/terms" className="underline hover:text-slate-400">Terms of Service</Link>.
                {' '}Your billing address on file with your payment method will be used as the return address on the postcard — please ensure it is current.
              </p>
            </div>

            {/* RIGHT SIDE: LIVE PREVIEW */}
            <div className="sticky top-6 lg:top-10 flex flex-col">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4 text-center lg:text-left">Live Preview (Back of Card)</h3>

              <div className="aspect-[6/4] bg-[#fdfbf7] rounded-sm shadow-2xl shadow-black/50 border border-slate-200 pt-2 pb-4 px-4 sm:pt-3 sm:pb-6 sm:px-6 relative overflow-hidden"
                   style={{ backgroundImage: 'url("https://www.transparenttextures.com/patterns/cream-paper.png")' }}>
                <div className="absolute left-[58%] top-[10%] bottom-[10%] w-px bg-amber-300/50"></div>
                <div className="absolute top-2 right-4 sm:top-3 sm:right-6 w-14 h-16 sm:w-16 sm:h-20 border-2 border-amber-300/60 flex items-center justify-center text-[10px] sm:text-xs text-slate-400 font-bold tracking-tighter text-center bg-white/50 p-1">
                  POSTAGE REQUIRED
                </div>

                <div className="flex h-full">
                   <div className="w-[55%] pr-4 sm:pr-6 flex flex-col">
                     <div className="font-sans text-slate-800 text-sm sm:text-base leading-relaxed whitespace-pre-wrap flex flex-col h-full">
                       <p className="font-bold mb-2">Dear Representative(s),</p>
                       <div className="flex-grow break-words">
                         {message || <span className="text-slate-300 italic">Your message will appear here...</span>}
                       </div>
                       <p className="mt-2">
                         Sincerely,<br/>
                         <span className="italic text-slate-500 font-medium">[Your Name]</span>
                       </p>
                     </div>
                   </div>

                   <div className="w-[45%] pl-4 sm:pl-8 flex items-center">
                      <div className="text-slate-800 font-mono text-[10px] sm:text-xs md:text-sm uppercase leading-loose w-full">
                        <p className="font-bold text-slate-900 border-b border-slate-200 pb-2 mb-2">Your Representative</p>
                        <p>U.S. House / Senate</p>
                        <p>Washington, DC</p>
                        <p className="mt-6 text-[9px] sm:text-[10px] text-slate-500 normal-case leading-tight">
                          From: Constituent<br/>
                          {address ? (address.length > 35 ? address.substring(0, 35) + '...' : address) : 'Your Address...'}
                        </p>
                      </div>
                   </div>
                </div>
              </div>

              {!isEdited && (
                <ShareButtons
                  url={shareUrl}
                  text="I sent a postcard to Congress. Add your voice:"
                  showCard
                  className="mt-auto pt-4"
                />
              )}
            </div>

          </div>
        </div>
      )}

      {/* STEP 2: SELECT REPS & CHECKOUT */}
      {step === 'select' && (
        <div className="max-w-6xl w-full mt-8 sm:mt-10 animate-in fade-in slide-in-from-bottom-8 duration-500 z-10 px-4">

          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white">Build your list.</h2>
            <p className="text-lg text-slate-300 mt-2">Search as many times as you like — add any rep to your list.</p>
          </div>

          {/* Search Again bar */}
          <div className="bg-[#fafaf7] rounded-2xl border border-amber-200/60 shadow-lg p-4 mb-8">
            <div className="rounded-xl overflow-hidden border border-slate-200 mb-3">
              <div role="radiogroup" aria-label="Level of government">
                <div className="flex gap-1 bg-slate-100 p-1 text-sm font-semibold">
                  {(['federal', 'state'] as const).map(lvl => {
                    const isActive = govLevels.has(lvl);
                    return (
                      <button
                        key={lvl}
                        type="button"
                        role="radio"
                        aria-checked={isActive}
                        onClick={() => setGovLevels(new Set([lvl]))}
                        className={`flex-1 py-1.5 rounded-lg transition-all cursor-pointer ${
                          isActive
                            ? lvl === 'federal' ? 'bg-red-700 text-white shadow-sm' : 'bg-amber-600 text-white shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        {lvl === 'federal' ? 'Federal' : 'State'}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="border-t border-slate-200" />
              <div role="radiogroup" aria-label="Search method">
                <div className="flex gap-1 bg-slate-100 p-1 text-sm font-semibold">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={searchMode === 'address'}
                    onClick={() => { setSearchMode('address'); window.history.replaceState(null, '', '?mode=address#select'); }}
                    className={`flex-1 py-1.5 rounded-lg transition-all ${searchMode === 'address' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    By Address
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={searchMode === 'name'}
                    onClick={() => { setSearchMode('name'); window.history.replaceState(null, '', '?mode=name#select'); }}
                    className={`flex-1 py-1.5 rounded-lg transition-all ${searchMode === 'name' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    By Name
                  </button>
                </div>
              </div>
            </div>
            <form onSubmit={handleSearchAgain} className="flex gap-2">
              {searchMode === 'name' && govLevels.has('state') && (
                <select
                  value={stateJurisdiction}
                  onChange={e => setStateJurisdiction(e.target.value)}
                  required
                  className="px-3 py-3 rounded-xl border border-slate-200 focus:ring-2 outline-none text-sm text-slate-900 bg-white"
                >
                  <option value="">State…</option>
                  {US_STATES.map(([abbr, label]) => (
                    <option key={abbr} value={abbr}>{label}</option>
                  ))}
                </select>
              )}
              <input
                type="text"
                value={searchMode === 'address' ? address : repName}
                onChange={(e) => searchMode === 'address' ? setAddress(e.target.value) : setRepName(e.target.value)}
                placeholder={searchMode === 'address' ? 'Search by address...' : 'Search by name...'}
                required
                className="flex-1 px-4 py-3 rounded-xl border border-slate-200 focus:ring-4 outline-none text-base text-slate-900"
              />
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 bg-slate-800 hover:bg-slate-700 disabled:bg-slate-300 text-white font-bold rounded-xl text-base transition-colors whitespace-nowrap active:scale-95"
              >
                {loading ? 'Searching...' : 'Search'}
              </button>
            </form>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider">Search Results</h3>
                {newResultsNotInCart.length > 0 && (
                  <button
                    onClick={() => addAllToCart(searchResults)}
                    className="text-sm font-bold text-amber-600 hover:text-amber-500 transition-colors"
                  >
                    Add All →
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {searchResults.map((rep, i) => renderRepCard(rep, i, false))}
              </div>
            </div>
          )}

          {/* Your List */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider">Your List ({cart.length})</h3>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-900/40 border border-amber-500/30 rounded-full text-amber-200 text-xs font-medium">
                <span>🏷️</span>
                <span>${(getPerCardAverage(cart.length) / 100).toFixed(2)}/card</span>
                {cart.length >= 3 && <span className="text-green-400">· Discount!</span>}
              </div>
            </div>

            {cart.length >= 1 && cart.length < 8 && newResultsNotInCart.length > 0 && (
              <div className="mb-4 px-4 py-3 bg-blue-900/20 border border-blue-500/30 rounded-xl text-blue-200 text-sm">
                Add 1 more for just <strong>${(getMarginalCost(cart.length) / 100).toFixed(2)}</strong> →{' '}
                {cart.length + 1} cards for <strong>${(getTotalPrice(cart.length + 1) / 100).toFixed(2)}</strong>
              </div>
            )}
            {cart.length === 8 && (
              <div className="mb-4 px-4 py-3 bg-green-900/20 border border-green-500/30 rounded-xl text-green-300 text-sm font-semibold">
                Maximum volume discount unlocked! $1.65 per additional card.
              </div>
            )}

            {cart.length > 10 && (
              <div className="mb-4 px-4 py-3 bg-amber-900/40 border border-amber-500/40 rounded-xl text-amber-200 text-sm font-medium">
                ⚠️ That's {cart.length} postcards · {getFormattedTotal(cart.length)} total — are you sure?
              </div>
            )}

            {cart.length === 0 ? (
              <div className="text-slate-400 text-center py-8 rounded-2xl border-2 border-dashed border-slate-700">
                Add representatives above to build your list.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {cart.map((rep, i) => renderRepCard(rep, i, true))}
              </div>
            )}
          </div>

          {/* Promo code */}
          <div className="mt-4 mb-3">
            {!showPromoInput ? (
              <button
                type="button"
                onClick={() => setShowPromoInput(true)}
                className="w-full py-3 rounded-xl border border-slate-600 text-slate-400 hover:border-slate-500 hover:text-slate-300 text-sm font-medium transition-colors"
              >
                🏷️ Have a promo code?
              </button>
            ) : (
              <div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={promoCode}
                    autoFocus
                    onChange={(e) => { setPromoCode(e.target.value); setAppliedPromo(null); setPromoError(''); }}
                    onKeyDown={(e) => e.key === 'Enter' && handleApplyPromo()}
                    placeholder="Enter promo code"
                    className="flex-1 px-4 py-3 rounded-xl border border-slate-600 bg-slate-800 text-white focus:ring-2 focus:ring-amber-400 outline-none text-sm"
                  />
                  <button
                    type="button"
                    onClick={handleApplyPromo}
                    disabled={promoLoading || !promoCode.trim()}
                    className="px-5 py-3 bg-slate-600 hover:bg-slate-500 disabled:bg-slate-700 text-white text-sm font-bold rounded-xl transition-colors whitespace-nowrap"
                  >
                    {promoLoading ? '...' : 'Apply'}
                  </button>
                </div>
                {appliedPromo && (
                  <p className="mt-2 text-sm text-green-400 font-semibold">✓ {appliedPromo.label} applied!</p>
                )}
                {promoError && (
                  <p className="mt-2 text-sm text-red-400 font-medium">✗ {promoError}</p>
                )}
              </div>
            )}
          </div>

          {/* Consent checkboxes */}
          <div className="flex flex-col gap-2 mt-4 text-sm text-slate-400">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" checked={okToContact} onChange={e => setOkToContact(e.target.checked)} className="w-4 h-4 rounded accent-red-700" />
              It&apos;s okay for us to contact you via email.
            </label>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" checked={subscribeNewsletter} onChange={e => setSubscribeNewsletter(e.target.checked)} className="w-4 h-4 rounded accent-red-700" />
              Subscribe to our newsletter.
            </label>
          </div>

          {/* Bottom Actions */}
          <div className="flex flex-col sm:flex-row items-center gap-4 mt-4">
            <button
              onClick={() => window.history.back()}
              className="px-6 py-3 text-slate-400 hover:text-white font-semibold rounded-xl transition-colors text-base"
            >
              ← Back
            </button>

            <div className="relative flex-1">
              {cart.length >= 3 && (
                <div className="absolute -top-3 -right-3 bg-green-500 text-white text-[10px] uppercase tracking-wider font-extrabold px-3 py-1 rounded-full shadow-lg transform rotate-6 z-20 animate-in zoom-in duration-300">
                  Discount Applied!
                </div>
              )}
              <button
                onClick={handleCheckout}
                disabled={cart.length === 0 || isCheckingOut}
                className="w-full px-10 py-4 bg-red-700 hover:bg-red-800 disabled:bg-slate-600 disabled:text-slate-400 text-white font-bold rounded-xl text-lg sm:text-xl transition-all shadow-lg hover:shadow-red-800/40 active:scale-95"
              >
                {isCheckingOut
                  ? 'Loading Secure Checkout...'
                  : `Checkout (${cart.length} ${cart.length === 1 ? 'rep' : 'reps'}) · $${(cartTotal(cart.length) / 100).toFixed(2)} →`
                }
              </button>
            </div>
          </div>

          <p className="text-xs text-slate-500 mt-4 text-center">
            By using this site, you agree to our <Link href="/terms" className="underline hover:text-slate-400">Terms of Service</Link>.
            {' '}Your billing address on file with your payment method will be used as the return address on the postcard — please ensure it is current.
          </p>

          {/* Live Preview */}
          {cart.length > 0 && (
            <div className="mt-10 max-w-2xl mx-auto w-full">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4 text-center">Live Preview (Back of Card)</h3>

              <div className="aspect-[6/4] bg-[#fdfbf7] rounded-sm shadow-2xl shadow-black/50 border border-slate-200 p-4 sm:p-6 relative overflow-hidden"
                   style={{ backgroundImage: 'url("https://www.transparenttextures.com/patterns/cream-paper.png")' }}>
                <div className="absolute left-[58%] top-[10%] bottom-[10%] w-px bg-amber-300/50"></div>
                <div className="absolute top-4 right-4 sm:top-6 sm:right-6 w-14 h-16 sm:w-16 sm:h-20 border-2 border-amber-300/60 flex items-center justify-center text-[10px] sm:text-xs text-slate-400 font-bold tracking-tighter text-center bg-white/50 p-1">
                  POSTAGE REQUIRED
                </div>

                <div className="flex h-full">
                  <div className="w-[55%] pr-4 sm:pr-6 pt-2 sm:pt-4 flex flex-col overflow-hidden">
                    <div className="font-sans text-slate-800 text-sm sm:text-base leading-relaxed whitespace-pre-wrap flex flex-col h-full">
                      <p className="font-bold mb-2">Dear Representative(s),</p>
                      <div className="flex-grow break-words overflow-hidden">
                        {message || <span className="text-slate-300 italic">Your message will appear here...</span>}
                      </div>
                      <p className="mt-2">
                        Sincerely,<br/>
                        <span className="italic text-slate-500 font-medium">[Your Name]</span>
                      </p>
                    </div>
                  </div>

                  <div className="w-[45%] pl-4 sm:pl-8 flex items-center">
                    {(() => {
                      const previewRep = cart[0];
                      const previewName = previewRep ? getRepFullName(previewRep) : 'Your Representative';
                      const isState = previewRep?.level === 'state';
                      const addr = isState ? previewRep.capitol_address : null;
                      const addrLine1 = addr?.line1 || (previewRep?.type === 'senator' ? 'U.S. Senate' : 'U.S. House of Representatives');
                      const addrCity = addr ? `${addr.city}, ${addr.state} ${addr.zip}` : `Washington, DC ${previewRep?.type === 'senator' ? '20510' : '20515'}`;
                      return (
                        <div className="text-slate-800 font-mono text-[10px] sm:text-xs md:text-sm uppercase leading-loose w-full pr-16 sm:pr-20">
                          <p className="font-bold text-slate-900 border-b border-slate-200 pb-2 mb-2">{previewName}</p>
                          <p>{addrLine1}</p>
                          <p>{addrCity}</p>
                          <p className="mt-6 text-[9px] sm:text-[10px] text-slate-500 normal-case leading-tight">
                            From: Constituent<br/>
                            {address ? (address.length > 35 ? address.substring(0, 35) + '...' : address) : 'Your Address...'}
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      )}
    </main>
  );
}
